import React, { useState } from 'react';
import { FiBell, FiSliders, FiClock } from 'react-icons/fi';
import { toast } from '../Toast';
import Toggle from '../Toggle';
import Dropdown from '../Dropdown';
import AccountNav from './AccountNav';

// UI only for now (user decision 2026-09-24): no api calls, no per-user preference model yet. Values are the spec's defaults.

type Channel = 'instant' | 'digest' | 'inApp';
const CHANNELS: { key: Channel; label: string }[] = [
    { key: 'instant', label: 'Instant email' },
    { key: 'digest', label: 'Digest' },
    { key: 'inApp', label: 'In app' },
];

interface EventRow {
    key: string;
    label: string;
    note?: string;
    // channels that are forced on and can't be changed
    locked?: Channel[];
}

const EVENTS: EventRow[] = [
    { key: 'approval', label: 'Announcement needs approval', note: 'Always on for approvers', locked: ['instant', 'digest', 'inApp'] },
    { key: 'question', label: 'New investor question' },
    { key: 'questionWaiting', label: 'Question waiting more than 48 hours' },
    { key: 'registryReport', label: 'Registry report ready' },
    { key: 'largeMovement', label: 'Large holder movement (over 1M shares)' },
    { key: 'campaignReport', label: 'Campaign report ready (7 days)' },
    { key: 'hubSignUp', label: 'New Website Hub sign‑up' },
    { key: 'pagePublished', label: 'Page published or saved as draft' },
    { key: 'security', label: 'Security alerts (new sign‑in, password change)', note: 'Always on', locked: ['instant', 'inApp'] },
];

interface Prefs {
    digest: boolean;
    browser: boolean;
    sms: boolean;
    matrix: { [event: string]: { [c in Channel]: boolean } };
    quiet: boolean;
    quietFrom: string;
    quietUntil: string;
    quietDays: string;
}

const on = (instant: boolean, digest: boolean, inApp: boolean) => ({ instant, digest, inApp });

const DEFAULTS: Prefs = {
    digest: true,
    browser: false,
    sms: true,
    matrix: {
        approval: on(true, true, true),
        question: on(true, true, true),
        questionWaiting: on(true, false, true),
        registryReport: on(false, true, true),
        largeMovement: on(true, true, true),
        campaignReport: on(false, true, true),
        hubSignUp: on(false, true, true),
        pagePublished: on(false, false, true),
        security: on(true, false, true),
    },
    quiet: true,
    quietFrom: '19:00',
    quietUntil: '07:00',
    quietDays: 'every',
};

// half-hour steps, "7:00 pm" labels
const TIMES = Array.from({ length: 48 }, (_, i) => {
    const h = Math.floor(i / 2);
    const m = i % 2 ? '30' : '00';
    const label = `${h % 12 === 0 ? 12 : h % 12}:${m} ${h < 12 ? 'am' : 'pm'}`;
    return { value: `${String(h).padStart(2, '0')}:${m}`, label };
});
const DAYS = [
    { value: 'every', label: 'Every day' },
    { value: 'weekdays', label: 'Weekdays' },
    { value: 'weekends', label: 'Weekends' },
];

const NotificationPreferences: React.FC = () => {
    const [prefs, setPrefs] = useState<Prefs>(DEFAULTS);
    const [savedPrefs, setSavedPrefs] = useState<Prefs>(DEFAULTS);
    const dirty = JSON.stringify(prefs) !== JSON.stringify(savedPrefs);

    const set = (patch: Partial<Prefs>) => setPrefs(p => ({ ...p, ...patch }));
    const setCell = (event: string, channel: Channel, value: boolean) =>
        setPrefs(p => ({ ...p, matrix: { ...p.matrix, [event]: { ...p.matrix[event], [channel]: value } } }));

    const save = () => {
        setSavedPrefs(prefs);
        toast.info('Preview only: this isn’t connected yet, so your preferences were not saved');
    };

    return <>
        <div className="page-header">
            <div>
                <h1>Notification Preferences</h1>
                <p>Choose what you hear about, and how. Approvals and security alerts are always on.</p>
            </div>
            <button type="button" className="btn-primary" onClick={save} disabled={!dirty}>Save Changes</button>
        </div>

        <div className="account-row">
            <AccountNav />

            <div className="account-main">
                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiBell /></span><h2>How You'd Like to Be Notified</h2></div>
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Morning email digest</div>
                            <div className="hint">One email at 8:00 am AWST with everything from the day before</div>
                        </div>
                        <Toggle label="Morning email digest" checked={prefs.digest} onChange={v => set({ digest: v })} />
                    </div>
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Browser notifications</div>
                            <div className="hint">Pop‑ups on your computer while Investi is open</div>
                        </div>
                        <Toggle label="Browser notifications" checked={prefs.browser} onChange={v => set({ browser: v })} />
                    </div>
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">SMS for urgent items</div>
                            <div className="hint">Approvals waiting more than 2 hours on a trading day</div>
                        </div>
                        <Toggle label="SMS for urgent items" checked={prefs.sms} onChange={v => set({ sms: v })} />
                    </div>
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiSliders /></span><h2>What to Notify Me About</h2></div>
                    <div className="matrix">
                        <div className="matrix-head">
                            <span />
                            {CHANNELS.map(c => <span key={c.key} className="cell">{c.label}</span>)}
                        </div>
                        {EVENTS.map(e => <div key={e.key} className="matrix-row">
                            <div className="text">
                                <div className="title">{e.label}</div>
                                {e.note && <div className="note">{e.note}</div>}
                            </div>
                            {CHANNELS.map(c => {
                                const locked = e.locked?.includes(c.key);
                                return <span key={c.key} className="cell">
                                    <Toggle label={`${e.label}: ${c.label}`} checked={prefs.matrix[e.key][c.key]} locked={locked} faded={locked}
                                        onChange={v => setCell(e.key, c.key, v)} />
                                </span>;
                            })}
                        </div>)}
                    </div>
                </section>

                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiClock /></span><h2>Quiet Hours</h2>
                        <Toggle label="Quiet hours" checked={prefs.quiet} onChange={v => set({ quiet: v })} />
                    </div>
                    <div className="fields three">
                        <div className="field">
                            <span className="label">From</span>
                            <Dropdown value={prefs.quietFrom} options={TIMES} onChange={v => set({ quietFrom: v })} disabled={!prefs.quiet} />
                        </div>
                        <div className="field">
                            <span className="label">Until</span>
                            <Dropdown value={prefs.quietUntil} options={TIMES} onChange={v => set({ quietUntil: v })} disabled={!prefs.quiet} />
                        </div>
                        <div className="field">
                            <span className="label">Days</span>
                            <Dropdown value={prefs.quietDays} options={DAYS} onChange={v => set({ quietDays: v })} disabled={!prefs.quiet} />
                        </div>
                    </div>
                    <p className="note">Security alerts and approvals still come through during quiet hours.</p>
                </section>
            </div>
        </div>
    </>;
};

export default NotificationPreferences;
